/**
 * QrTotpSetup — UI enroll MFA TOTP (F3-01).
 *
 * Renderiza:
 *   1. QR code (data URI base64 PNG generado por backend `/api/mfa/enroll`)
 *   2. Preview del secret (primeros 4 + últimos 4 chars — fallback manual para apps sin cámara)
 *   3. Input 6 dígitos OTP + botón verify
 *
 * Callers: `routes/app/MfaSetup.tsx`
 *
 * PHI: el QR data URI contiene un secret TOTP — es credencial, NO PHI clínico.
 * Aún así no se persiste en localStorage ni se loggea (regla PHI §1).
 */

import { useId } from 'react'
import type { FormEvent } from 'react'
import { useForm } from 'react-hook-form'
import { ShieldCheck, Smartphone } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

interface QrTotpSetupProps {
  /** Data URI PNG del QR code. Ej: `data:image/png;base64,iVBORw0...`. */
  qrDataUri: string
  /** Preview del secret (primeros 4 + últimos 4 chars base32). */
  secretPreview: string
  /** Handler verify OTP. Lanza Error si falla — parent maneja toast. */
  onVerify: (otp: string) => Promise<void>
  /** Loading flag para botón submit. */
  isVerifying: boolean
  /** Mensaje de error externo (ej. OTP inválido devuelto por backend). */
  errorMessage?: string | null
}

interface FormValues {
  otp: string
}

export function QrTotpSetup({
  qrDataUri,
  secretPreview,
  onVerify,
  isVerifying,
  errorMessage,
}: QrTotpSetupProps) {
  const headingId = useId()
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    defaultValues: { otp: '' },
  })

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    await handleSubmit(async (values) => {
      await onVerify(values.otp.trim())
      reset()
    })(e)
  }

  return (
    <section
      aria-labelledby={headingId}
      data-testid="qr-totp-setup"
      className="flex flex-col gap-5"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[color:var(--color-fitlab-blue)]/10">
          <ShieldCheck className="h-5 w-5 text-[color:var(--color-fitlab-blue)]" aria-hidden />
        </div>
        <div>
          <h2 id={headingId} className="text-lg font-semibold text-slate-900">
            Escanea el código QR
          </h2>
          <p className="mt-0.5 text-sm text-slate-600">
            Usa Google Authenticator, Authy o 1Password para registrar FITLAB.
          </p>
        </div>
      </div>

      {/* QR + instrucciones lado a lado (sm+) o stacked (mobile) */}
      <div className="flex flex-col items-start gap-6 sm:flex-row sm:items-start">
        <div className="flex w-full flex-col items-center gap-2 rounded-lg border border-slate-200 bg-white p-4 sm:w-auto">
          <img
            src={qrDataUri}
            alt="Código QR para configurar autenticación de dos pasos en tu app"
            className="h-44 w-44 sm:h-48 sm:w-48"
            data-testid="qr-image"
          />
          <p className="text-center text-xs text-slate-500">Escanea con tu app de autenticación</p>
        </div>

        <ol className="flex-1 space-y-3 text-sm text-slate-700">
          <li className="flex gap-2">
            <Smartphone
              className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--color-fitlab-green)]"
              aria-hidden
            />
            <span>
              Abre <strong className="font-semibold text-slate-900">Google Authenticator</strong>,{' '}
              <strong className="font-semibold text-slate-900">Authy</strong> o{' '}
              <strong className="font-semibold text-slate-900">1Password</strong>.
            </span>
          </li>
          <li>Escanea el código QR con la cámara de la app.</li>
          <li>Ingresa el código de 6 dígitos que genera la app para confirmar la configuración.</li>
          <li className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
            ¿Sin cámara? Ingresa el código manualmente:{' '}
            <code
              className="rounded bg-white px-1 font-mono text-[11px] text-amber-900"
              data-testid="qr-secret-preview"
            >
              {secretPreview}
            </code>
          </li>
        </ol>
      </div>

      <form onSubmit={onSubmit} className="flex flex-col gap-3" noValidate>
        <Input
          label="Código de verificación (6 dígitos)"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]{6}"
          maxLength={6}
          required
          placeholder="123456"
          error={errors.otp?.message ?? (errorMessage ? errorMessage : undefined)}
          data-testid="otp-input"
          {...register('otp', {
            required: 'Ingresa el código de 6 dígitos',
            pattern: {
              value: /^\d{6}$/,
              message: 'El código debe tener 6 dígitos',
            },
            onChange: (e) => {
              e.target.value = e.target.value.replace(/\D/g, '').slice(0, 6)
            },
          })}
        />

        <Button
          type="submit"
          loading={isVerifying}
          size="lg"
          className="w-full"
          data-testid="otp-submit"
        >
          Verificar y activar
        </Button>
      </form>
    </section>
  )
}
